'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/routes';
import { readSiteConsent, subscribeSiteConsent, writeSiteConsent } from '@/lib/privacy/consent-client';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    const update = () => setVisible(readSiteConsent() === null);
    update();
    return subscribeSiteConsent(update);
  }, []);

  function choose(value: 'essential' | 'analytics') {
    const saved = writeSiteConsent(value);
    setSaveFailed(!saved);
    setVisible(!saved);
  }
  if (!visible) return null;
  return (
    <section aria-label="Privacy choices" className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-3xl rounded-2xl border border-blush bg-white p-5 shadow-2xl">
      <h2 className="font-headline text-xl text-charcoal">Your privacy choices</h2>
      <p className="mt-2 text-sm leading-6 text-charcoal/75">Essential storage keeps your cart, sign-in, security, reader and music preferences working. With your permission, analytics helps improve the shop. You can continue without analytics.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" onClick={() => choose('essential')}>Essential only</Button>
        <Button type="button" className="bg-[#5F8769] text-white hover:bg-[#547A5F]" onClick={() => choose('analytics')}>Allow analytics</Button>
        <Link href={ROUTES.privacy} className="text-sm font-medium text-terra underline underline-offset-4">Privacy details</Link>
      </div>
      {saveFailed ? <p role="status" className="mt-3 text-sm">Your choice could not be saved. Analytics is off in this tab. Enable browser storage to save a choice.</p> : null}
    </section>
  );
}

export function ConsentSettings() {
  const [value, setValue] = useState<'essential' | 'analytics' | null>(null);
  const [referralMessage, setReferralMessage] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  useEffect(() => {
    const update = () => setValue(readSiteConsent());
    update();
    return subscribeSiteConsent(update);
  }, []);
  function choose(next: 'essential' | 'analytics') {
    setSaveFailed(!writeSiteConsent(next));
    setValue(readSiteConsent());
  }
  async function clearReferral() {
    await fetch('/api/affiliate/attribution', { method: 'DELETE', credentials: 'include' }).catch(() => null);
    setReferralMessage('Referral attribution cookie removed.');
  }
  return (
    <div className="rounded-2xl border border-blush/70 bg-cream p-5">
      <p className="font-semibold text-charcoal">Current choice: {value === 'analytics' ? 'Essential storage + analytics' : value === 'essential' ? 'Essential storage only' : 'No choice saved'}</p>
      <div className="mt-3 flex flex-wrap gap-3">
        <Button type="button" variant="outline" onClick={() => choose('essential')}>Use essential only</Button>
        <Button type="button" className="bg-[#5F8769] text-white hover:bg-[#547A5F]" onClick={() => choose('analytics')}>Allow analytics</Button>
        <Button type="button" variant="outline" onClick={() => void clearReferral()}>Remove referral tracking</Button>
      </div>
      {saveFailed ? <p role="status" className="mt-3 text-sm">Your choice could not be saved. Analytics is off in this tab. Enable browser storage to save a choice.</p> : null}
      {referralMessage ? <p className="mt-3 text-sm text-[#315f44]">{referralMessage}</p> : null}
    </div>
  );
}
